
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorInfo } from "@/components/contractor/ContractorInfo";
import { ContractorSchema } from "@/components/contractor/ContractorSchema";
import { LoadingState } from "@/components/LoadingState";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getPlaceholderImage } from "@/utils/imageUtils";
import { ContractorHeader } from "@/components/contractor/details/ContractorHeader";
import { ContractorImage } from "@/components/contractor/details/ContractorImage";
import { ContractorContact } from "@/components/contractor/details/ContractorContact";
import { ContractorRating } from "@/components/contractor/details/ContractorRating";
import { trackContractorView } from "@/utils/metaPixel";

const ContractorDetails = () => {
  const { location, serviceType, businessSlug, contractorId } = useParams();
  const navigate = useNavigate();
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("We couldn't find the contractor you're looking for.");

  console.log('ContractorDetails mounting with params:', {
    location,
    serviceType,
    businessSlug,
    contractorId
  });

  // Clean up slug from trailing commas if present
  const cleanSlug = (slug?: string) => {
    if (!slug) return slug;
    return slug.replace(/-,$/g, '');
  };

  // Determine if we're using the old or new URL pattern
  const isOldUrlPattern = !!contractorId && !location && !serviceType && !businessSlug;
  const slug = cleanSlug(isOldUrlPattern ? contractorId : businessSlug);

  // Handle invalid parameters
  if ((!location || !serviceType || !businessSlug) && !isOldUrlPattern) {
    console.error('Invalid URL parameters. Missing required path parameters.', {
      location, 
      serviceType, 
      businessSlug, 
      isOldUrlPattern
    });
    return (
      <div className="container py-8 px-4">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">Contractor Not Found</h2>
          <p>We couldn't find the contractor you're looking for. The URL may be incorrect.</p>
          <a href="/" className="mt-4 text-blue-600 hover:underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Fix serviceType variants
  const normalizeServiceType = (service?: string) => {
    if (!service) return service;
    
    const serviceMap: Record<string, string> = {
      'general-contractor': 'general-contracting',
      'general-contractors': 'general-contracting',
      'house-cleaners': 'house-cleaning',
      'tree-services': 'tree-service',
    };
    
    return serviceMap[service.toLowerCase()] || service;
  };
  
  const normalizedServiceType = normalizeServiceType(serviceType);

  const { data: contractor, isLoading, error } = useQuery({
    queryKey: ["contractor", slug],
    queryFn: async () => {
      console.log('Fetching contractor data for slug:', slug);
      
      try {
        const { data, error } = await supabase
          .from("contractors")
          .select("*")
          .eq("slug", slug)
          .single();

        if (error) {
          console.error('Error fetching contractor:', error);
          setIsError(true);
          setErrorMessage("We couldn't find the contractor you're looking for. They may no longer be available.");
          throw error;
        }

        if (!data) {
          console.error('No contractor found with slug:', slug);
          setIsError(true);
          setErrorMessage("We couldn't find the contractor you're looking for. They may no longer be available.");
          throw new Error('Contractor not found');
        }

        console.log('Successfully fetched contractor:', data);
        
        // Track contractor view with Meta Pixel
        trackContractorView(data.id.toString(), data.name);
        
        return data;
      } catch (err) {
        console.error('Failed to fetch contractor data:', err);
        setIsError(true);
        throw err;
      }
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Redirect if using old URL pattern and we have the contractor data
  useEffect(() => {
    if (isOldUrlPattern && contractor) {
      const correctServiceType = contractor.service_type.toLowerCase().replace(/\s+/g, '-');
      const correctLocation = contractor.location || 'oklahoma-city';
      const newPath = `/${correctLocation}/${correctServiceType}/${contractor.slug}`;
      console.log('Redirecting from old URL pattern to new path:', newPath);
      navigate(newPath, { replace: true });
    }
  }, [contractor, isOldUrlPattern, navigate]);

  // Redirect if service type in URL doesn't match contractor's service type (for new URL pattern)
  useEffect(() => {
    if (!isOldUrlPattern && contractor && serviceType) {
      const correctServiceType = contractor.service_type.toLowerCase().replace(/\s+/g, '-');
      const correctLocation = contractor.location || 'oklahoma-city';
      
      // Allow certain service type variations to be equivalent
      const allowedVariants = {
        'general-contractor': 'general-contracting',
        'general-contractors': 'general-contracting',
      };
      
      const isServiceTypeMatch = 
        serviceType === correctServiceType || 
        (allowedVariants[serviceType] === correctServiceType) ||
        (allowedVariants[correctServiceType] === serviceType);
      
      if (!isServiceTypeMatch || location !== correctLocation) {
        const newPath = `/${correctLocation}/${correctServiceType}/${businessSlug}`;
        console.log('Redirecting to correct path:', newPath);
        navigate(newPath, { replace: true });
      }
    }
  }, [contractor, serviceType, location, businessSlug, navigate, isOldUrlPattern]);

  console.log('ContractorDetails render state:', {
    isLoading,
    hasError: !!error || isError,
    hasData: !!contractor
  });

  if (isLoading) {
    console.log('Showing loading state...');
    return (
      <div className="container py-8 px-4">
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingState message="Loading contractor details..." />
        </div>
      </div>
    );
  }

  if (error || isError || !contractor) {
    console.error('Error or no contractor found:', error);
    return (
      <div className="container py-8 px-4">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">Contractor Not Found</h2>
          <p>{errorMessage}</p>
          <a href="/" className="mt-4 text-blue-600 hover:underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Format service type from contractor data
  const formattedServiceType = contractor.service_type;
  const urlServiceType = contractor.service_type.toLowerCase().replace(/\s+/g, '-');

  const breadcrumbItems = [
    {
      label: formattedServiceType,
      href: `/services/${urlServiceType}`
    },
    {
      label: contractor.name
    }
  ];

  // Get city from location
  const city = location
    ?.split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "Oklahoma City";

  // Determine the best image URL to use
  const imageUrl = contractor.logo_link || contractor.gmb_link || getPlaceholderImage(contractor.service_type);

  // Construct absolute URL for canonical
  const canonicalUrl = `/${location}/${urlServiceType}/${contractor.slug}`;

  return (
    <div className="container py-8 px-4">
      <Breadcrumbs items={breadcrumbItems} />
      <SEO
        title={`${contractor.name} - ${contractor.service_type} in ${city}`}
        description={`Learn more about ${contractor.name}, providing professional ${contractor.service_type} services in ${city}. View ratings, reviews, and contact information.`}
        canonicalUrl={canonicalUrl}
        ogImage={imageUrl}
      />

      <div className="mb-8">
        <ContractorHeader 
          name={contractor.name} 
          serviceType={formattedServiceType} 
          city={city} 
          featured={contractor.featured} 
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <ContractorImage 
            imageUrl={imageUrl}
            name={contractor.name}
            serviceType={contractor.service_type}
            city={city}
          />
        </div>
        <div className="md:col-span-2 space-y-8">
          <ContractorRating 
            rating={contractor.rating || 0} 
            reviewCount={contractor.review_count || 0} 
          />
          
          <ContractorContact 
            address={contractor.address}
            phone={contractor.phone}
            website={contractor.website}
            city={city}
            businessId={contractor.id}
            businessName={contractor.name}
          />
        </div>
      </div>

      <ContractorSchema 
        contractor={contractor}
        city={city}
        location={location || 'oklahoma-city'}
        serviceType={urlServiceType}
        businessSlug={contractor.slug}
        imageUrl={imageUrl}
      />
    </div>
  );
};

export default ContractorDetails;
